import MuxPlayer from '@mux/mux-player-react'
import {
  Button,
  Card,
  CardContent,
  CardTitle,
  Dialog,
  DialogContent,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Image,
} from '@repo/shared-client/components/ui'
import { useIsMobile } from '@repo/shared-client/hooks'
import { useNavigate } from '@tanstack/react-router'
import LogoSymbol from '@web/src/components/primatives/logo-symbol/logo-symbol'
import SFMediaAiDetection from '@web/src/components/primatives/sf-media-ai-detection/sf-media-ai-detection'
import MediaAdminEditDialog from '@web/src/features/media/media-admin-edit/media-admin-edit-dialog'
import MediaAdminEditMenuItems from '@web/src/features/media/media-admin-edit/media-admin-edit-menu-items'
import { useTranslation } from '@web/src/lib/intl/react'
import { ChevronDownIcon, ChevronUpIcon, EllipsisIcon, XIcon } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import type { MediaTypeEnum, ReviewEnum } from 'types/graphql'
import { useAuth } from '../../../hooks/use-auth'
import ReportCreate from '../../report/report-create/report-create'

type SimpleMediaImageViewerProps = {
  open?: boolean
  onOpenChange?: (details: { open: boolean }) => void
  modal?: boolean
  disablePointerDismissal?: boolean
  attachedMedia: boolean
  imageIndex?: number
  placeId?: string
  publicationId?: string
  vendorMarketId?: string
  conversationId?: string
  chatRoomId?: string
  messageId?: string
  mediaArray?: {
    id?: string
    fileName?: string
    mediaType?: MediaTypeEnum
    reviewStatus?: ReviewEnum
    imageUrlSmall?: string
    imageUrlMedium?: string
    imageUrlLarge?: string
    videoPlaybackToken?: string
    videoThumbnailToken?: string
    aiScore?: number | null
  }[]
  onMediaChange?: (id: string) => void
}

const SimpleMediaImageViewer = (props: SimpleMediaImageViewerProps) => {
  const { t } = useTranslation()
  const { isAuthenticated, hasPermission } = useAuth()
  const { attachedMedia, imageIndex, mediaArray = [], open, onOpenChange, modal, disablePointerDismissal } = props
  const isMobile = useIsMobile()
  const containerRef = useRef<HTMLDivElement | null>(null)
  const muxPlayerRefs = useRef<Map<string, { pause: () => void; currentTime: number; load: () => void }>>(new Map())
  const scrollRafRef = useRef<number | null>(null)
  const initialScrollAppliedRef = useRef(false)
  const lastReportedIndexRef = useRef<number>(-1)

  const initialIndex = Math.max(0, Math.min(mediaArray.length - 1, imageIndex ?? 0))
  const [currentIndex, setCurrentIndex] = useState(initialIndex)
  const [reportModal, setReportModal] = useState(false)
  const [reportMediaId, setReportMediaId] = useState<string | undefined>()
  const [adminEditState, setAdminEditState] = useState<{ open: boolean; mediaId?: string }>({
    open: false,
    mediaId: undefined,
  })
  const navigate = useNavigate()

  const activeMedia = mediaArray[currentIndex]

  const reportCurrentIndex = useCallback(() => {
    const container = containerRef.current
    if (!container || mediaArray.length === 0) return

    const viewportHeight = container.clientHeight || 1
    const nextIndex = Math.max(0, Math.min(mediaArray.length - 1, Math.round(container.scrollTop / viewportHeight)))
    if (nextIndex !== lastReportedIndexRef.current) {
      lastReportedIndexRef.current = nextIndex
      setCurrentIndex(nextIndex)
    }
  }, [mediaArray.length])

  const handleScroll = useCallback(() => {
    if (scrollRafRef.current != null) return
    scrollRafRef.current = window.requestAnimationFrame(() => {
      scrollRafRef.current = null
      muxPlayerRefs.current.forEach((player) => {
        if (player.currentTime > 0) {
          player.pause()
          player.currentTime = 0
          player.load()
        }
      })
      reportCurrentIndex()
    })
  }, [reportCurrentIndex])

  useEffect(() => {
    if (open !== true || mediaArray.length === 0) return
    const clampedIndex = Math.max(0, Math.min(mediaArray.length - 1, imageIndex ?? 0))
    setCurrentIndex(clampedIndex)
    lastReportedIndexRef.current = clampedIndex
  }, [open, imageIndex, mediaArray.length])

  useEffect(() => {
    if (open === false) {
      initialScrollAppliedRef.current = false
      return
    }
    if (initialScrollAppliedRef.current) return
    if (mediaArray.length === 0) return

    const clampedIndex = Math.max(0, Math.min(mediaArray.length - 1, imageIndex ?? 0))
    lastReportedIndexRef.current = clampedIndex
    setCurrentIndex(clampedIndex)

    const tryScroll = () => {
      const el = containerRef.current?.querySelector(`[data-media-index="${clampedIndex}"]`)
      if (el) {
        el.scrollIntoView({ block: 'start', behavior: 'auto' })
        initialScrollAppliedRef.current = true
        return true
      }
      return false
    }

    if (!tryScroll()) {
      const rafId = requestAnimationFrame(() => {
        tryScroll()
      })
      const timeoutId = setTimeout(tryScroll, 100)
      return () => {
        cancelAnimationFrame(rafId)
        clearTimeout(timeoutId)
      }
    }
  }, [open, imageIndex, mediaArray.length])

  useEffect(() => {
    if (open === false) return
    if (mediaArray.length === 0) return

    const clampedIndex = Math.max(0, Math.min(mediaArray.length - 1, imageIndex ?? 0))
    const container = containerRef.current
    if (!container) return

    const viewportHeight = container.clientHeight || 1
    const targetTop = clampedIndex * viewportHeight
    if (Math.abs(container.scrollTop - targetTop) > viewportHeight * 0.5) {
      const el = container.querySelector(`[data-media-index="${clampedIndex}"]`)
      el?.scrollIntoView({ block: 'start', behavior: 'auto' })
    }
    setCurrentIndex(clampedIndex)
  }, [open, imageIndex, mediaArray.length])

  useEffect(() => {
    return () => {
      if (scrollRafRef.current != null) {
        window.cancelAnimationFrame(scrollRafRef.current)
      }
    }
  }, [])

  const scrollToIndex = useCallback(
    (nextIndex: number) => {
      const container = containerRef.current
      if (!container || mediaArray.length === 0) return

      const viewportHeight = container.clientHeight || 1
      const clampedIndex = Math.max(0, Math.min(mediaArray.length - 1, nextIndex))
      container.scrollTo({ top: clampedIndex * viewportHeight, behavior: 'smooth' })
    },
    [mediaArray.length]
  )

  const closeDialog = () => {
    props?.onOpenChange?.({ open: false })
  }

  const handleOnClose = (e: React.MouseEvent) => {
    closeDialog()
    e?.stopPropagation()
  }

  const menuSide = isMobile ? 'left' : 'right'

  return (
    <>
      <Dialog
        disablePointerDismissal={disablePointerDismissal}
        modal={modal}
        onOpenChange={(nextOpen) => onOpenChange?.({ open: nextOpen })}
        open={open}
      >
        <DialogContent
          className="flex h-[100dvh] max-h-[100dvh] max-w-none gap-0 overflow-hidden rounded-none border-0 bg-black p-0 sm:max-w-none"
          initialFocus={false}
          showCloseButton={false}
        >
          <div className="relative flex min-h-0 flex-1 overflow-hidden px-0 md:px-2">
            <div
              className="h-[100dvh] w-full snap-y snap-mandatory overflow-y-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
              onScroll={handleScroll}
              ref={containerRef}
              style={{ msOverflowStyle: 'none', overscrollBehaviorY: 'contain' }}
            >
              <Button
                aria-label={t('Close')}
                className="fixed top-3 left-3 z-20 rounded-full bg-black/50 text-white hover:bg-black/70"
                onClick={(e) => handleOnClose(e)}
                size="icon"
                type="button"
                variant="ghost"
              >
                <XIcon />
              </Button>

              {mediaArray.length > 1 && (
                <div className="fixed top-1/2 right-3 z-20 hidden -translate-y-1/2 flex-col gap-3 md:flex md:flex-col">
                  <Button
                    aria-label={t('Previous media')}
                    className="rounded-full bg-black/50 text-white hover:bg-black/70"
                    disabled={currentIndex <= 0}
                    onClick={() => scrollToIndex(currentIndex - 1)}
                    size="icon"
                    type="button"
                    variant="ghost"
                  >
                    <ChevronUpIcon />
                  </Button>
                  <Button
                    aria-label={t('Next media')}
                    className="rounded-full bg-black/50 text-white hover:bg-black/70"
                    disabled={currentIndex >= mediaArray.length - 1}
                    onClick={() => scrollToIndex(currentIndex + 1)}
                    size="icon"
                    type="button"
                    variant="ghost"
                  >
                    <ChevronDownIcon />
                  </Button>
                </div>
              )}

              {mediaArray.map((media, idx) => {
                const mediaIsImage = media.mediaType === 'IMAGE' || !hasPermission('media:view-video')
                return (
                  <div
                    className="relative flex h-[100dvh] w-full snap-start bg-black"
                    data-media-index={idx}
                    key={media.id ?? idx}
                  >
                    <div className="relative flex h-full w-full items-center justify-center">
                      <SFMediaAiDetection aiScore={media.aiScore} className="absolute top-3 right-3 z-20" />

                      {!!isAuthenticated && (
                        <DropdownMenu>
                          <DropdownMenuTrigger
                            className="absolute top-1/2 right-3 -translate-y-1/2 md:right-auto md:left-1"
                            render={
                              <Button
                                aria-label={t('More actions')}
                                className="rounded-full bg-black/50 text-white hover:bg-black/70"
                                size="icon"
                                type="button"
                                variant="ghost"
                              />
                            }
                          >
                            <EllipsisIcon />
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="start" side={menuSide} sideOffset={8}>
                            <DropdownMenuItem
                              onClick={() => {
                                setReportMediaId(media.id)
                                setReportModal(true)
                              }}
                            >
                              {t('Report Media')}
                            </DropdownMenuItem>
                            <MediaAdminEditMenuItems
                              mediaId={media.id ?? ''}
                              onOpen={() => setAdminEditState({ open: true, mediaId: media.id })}
                            />
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}

                      {mediaIsImage ? (
                        <>
                          <Image
                            alt={t('Gallery media')}
                            className="h-full w-full object-contain"
                            src={media.imageUrlLarge ?? undefined}
                          />

                          {media.mediaType === 'VIDEO' && (
                            <Card className="absolute top-1/2 left-1/2 z-10 min-w-[70%] -translate-x-1/2 -translate-y-1/2 md:min-w-[40%]">
                              <CardContent className="flex flex-col items-center justify-center gap-2 py-6">
                                <CardTitle className="text-center text-2xl md:text-4xl">
                                  {t('Want to watch this video?')}
                                </CardTitle>
                                <p className="text-center">{t('Become a supporter of the site')}</p>
                                <LogoSymbol imageClassName="w-10" />
                                <p className="text-center">
                                  {t('It offers many benefits along with the ablity to watch videos.')}
                                </p>
                                <Button className="mt-6" onClick={() => navigate({ to: '/supporter' })} type="button">
                                  {t('Support Spicy Friends')}
                                </Button>
                              </CardContent>
                            </Card>
                          )}
                        </>
                      ) : (
                        <MuxPlayer
                          playbackId={media.fileName ?? undefined}
                          ref={(el) => {
                            const key = media.id ?? `media-${idx}`
                            if (el) muxPlayerRefs.current.set(key, el)
                            else muxPlayerRefs.current.delete(key)
                          }}
                          style={{ height: '100%', width: '100%' }}
                          tokens={{
                            playback: media.videoPlaybackToken ?? undefined,
                            thumbnail: media.videoThumbnailToken ?? undefined,
                          }}
                        />
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>

          <ReportCreate
            onOpenChange={(e) => setReportModal(e.open)}
            open={reportModal}
            parentTargetId={
              props.messageId
                ? (props.chatRoomId ?? props.conversationId)
                : (props.placeId ?? props.publicationId ?? props.vendorMarketId)
            }
            parentTargetKind={
              props.messageId
                ? 'CONVERSATION'
                : props.placeId
                  ? 'PLACE'
                  : props.publicationId
                    ? 'PUBLICATION'
                    : props.vendorMarketId
                      ? 'VENDOR_MARKET'
                      : undefined
            }
            targetId={reportMediaId ?? activeMedia?.id}
            targetKind={attachedMedia ? 'ATTACHED_MEDIA' : 'MEDIA'}
          />
        </DialogContent>
      </Dialog>
      <MediaAdminEditDialog
        isAttached={attachedMedia}
        mediaId={adminEditState.mediaId ?? ''}
        onMediaChange={props.onMediaChange}
        onOpenChange={(e) => setAdminEditState((prev) => ({ ...prev, open: e.open }))}
        open={adminEditState.open}
      />
    </>
  )
}

export default SimpleMediaImageViewer
